// Texts for call forwarding (settings, banner, history). Pure: tested.

import type { ForwardInfo, ForwardMode } from '../api';
import { formatPhone } from './number';

/** Seconds the app rings before forwarding "if I don't answer" (add-on rule). */
export const FORWARD_AFTER_SEC = 15;

export function forwardHelp(mode: ForwardMode): string {
  switch (mode) {
    case 'off':
      return 'Send incoming calls to another phone, like your mobile.';
    case 'noanswer':
      return `Your CloudPhone devices ring for ${FORWARD_AFTER_SEC} seconds, then the call goes to this number. Forwarded calls use your call credit.`;
    case 'always':
      return 'Incoming calls go straight to this number; your CloudPhone devices don’t ring. Forwarded calls use your call credit.';
  }
}

/** Status line under the setting (null = nothing to say). */
export function forwardStatus(f: ForwardInfo | null): { tone: 'ok' | 'bad'; text: string } | null {
  if (!f || f.mode === 'off') return null;
  if (!f.available) return { tone: 'bad', text: 'Forwarding isn’t available on the server yet. Ask your administrator.' };
  if (f.problem) return { tone: 'bad', text: f.problem };
  if (!f.active) return { tone: 'ok', text: 'Setting up… this takes a few seconds.' };
  return {
    tone: 'ok',
    text: `On: calls go to ${formatPhone(f.number)}${f.mode === 'noanswer' ? ` after ${FORWARD_AFTER_SEC} seconds` : ''}. If it’s busy or can’t be reached, callers can leave a voicemail here.`,
  };
}

/** Banner while every call is forwarded (the person would otherwise wonder why nothing rings). */
export function forwardBanner(f: ForwardInfo | null, dnd: boolean): string | null {
  if (!f || f.mode !== 'always' || !f.active || dnd) return null;
  return `Calls are forwarded to ${formatPhone(f.number)}.`;
}

/** Voicemail note in Settings, which depends on forwarding. */
export function voicemailNote(f: ForwardInfo | null): string {
  if (f?.active && f.mode !== 'off') {
    return 'Voicemail takes calls your forward number can’t (busy, unreachable). Messages up to 3 minutes, kept 90 days.';
  }
  return 'Calls you don’t answer within 25 seconds go to voicemail (up to 3 minutes; kept 90 days).';
}
